import { useEffect } from 'react'
import { proxy, useSnapshot } from 'valtio'

export type HelpTutorial = {
  id: string
  groupId: string
  title: string
  description: string
  durationSeconds: number
  // YouTube video id (played via embed).
  youtubeId: string
  // Optional custom cover uploaded to GCS. Falls back to the YouTube thumbnail.
  poster?: string
  guideSectionIds?: string[]
  guideTopicIds?: string[]
}

export type HelpTutorialGroup = {
  id: string
  title: string
  description: string
}

type TutorialConfig = {
  groups: HelpTutorialGroup[]
  tutorials: HelpTutorial[]
}

export type TutorialsStatus = 'idle' | 'loading' | 'loaded' | 'error'

// Tutorials come entirely from the remote config on GCS. No bundled fallback:
// GCS + YouTube are the single source of truth.
const state = proxy<TutorialConfig & { status: TutorialsStatus }>({
  groups: [],
  tutorials: [],
  status: 'idle',
})

let fetchStarted = false

const loadTutorials = async () => {
  if (fetchStarted) return
  fetchStarted = true

  const url = import.meta.env.VITE_TUTORIALS_CONFIG_URL as string | undefined
  if (!url) {
    state.status = 'error'
    console.error('VITE_TUTORIALS_CONFIG_URL is not set; cannot load tutorials.')
    return
  }

  state.status = 'loading'
  try {
    // Cache-buster so edits to the GCS object show up without waiting for the
    // default public-object Cache-Control window to expire.
    const separator = url.includes('?') ? '&' : '?'
    const res = await fetch(`${url}${separator}t=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) throw new Error(`Tutorials config request failed: ${res.status}`)

    const data = (await res.json()) as TutorialConfig
    state.groups = Array.isArray(data.groups) ? data.groups : []
    state.tutorials = Array.isArray(data.tutorials) ? data.tutorials : []
    state.status = 'loaded'
  } catch (err) {
    state.status = 'error'
    console.error('Failed to load tutorials config from GCS.', err)
  }
}

// Subscribe a component to the tutorials config and trigger the one-time remote load.
export const useTutorials = () => {
  useEffect(() => {
    loadTutorials()
  }, [])

  const snap = useSnapshot(state)
  return {
    groups: snap.groups as HelpTutorialGroup[],
    tutorials: snap.tutorials as HelpTutorial[],
    status: snap.status,
  }
}

export const formatTutorialDuration = (durationSeconds: number) => `${durationSeconds} sec`

// Read the current (remote-loaded) tutorials. Callers must be inside a component
// tree that mounts useTutorials() so re-renders pick up the data once it loads.
export const getTutorialsForSection = (sectionId: string) =>
  state.tutorials.filter((tutorial) => tutorial.guideSectionIds?.includes(sectionId))

export const getTutorialsForTopic = (topicId: string) =>
  state.tutorials.filter((tutorial) => tutorial.guideTopicIds?.includes(topicId))

// --- YouTube helpers -------------------------------------------------------

// YouTube thumbnail files, largest first. maxresdefault (1280x720) and sddefault
// (640x480) are only generated for some videos and 404 otherwise; hqdefault
// (480x360) always exists, so it terminates the chain.
// Note maxresdefault is natively 16:9 — the smaller ones are 4:3 and get cropped
// (and upscaled) by the 16:9 card, which is why they look soft.
const YOUTUBE_THUMBNAIL_FILES = ['maxresdefault.jpg', 'sddefault.jpg', 'hqdefault.jpg'] as const

export const getYoutubeThumbnail = (youtubeId: string, file: string = 'hqdefault.jpg') =>
  `https://img.youtube.com/vi/${youtubeId}/${file}`

// autoplay=1 so the click-to-load facade starts playback when the modal opens.
export const getYoutubeEmbedUrl = (youtubeId: string) =>
  `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&playsinline=1&modestbranding=1`

// Poster candidates for cards/facade, best first. An explicit `poster` URL (custom
// cover on GCS) wins; then YouTube thumbnails from largest to smallest. Callers
// should advance to the next candidate when an image fails to load.
export const getTutorialPosterCandidates = (tutorial: HelpTutorial) => [
  ...(tutorial.poster ? [tutorial.poster] : []),
  ...YOUTUBE_THUMBNAIL_FILES.map((file) => getYoutubeThumbnail(tutorial.youtubeId, file)),
]
